import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

export type LocalBook = {
  id: string;
  title: string;
  author: string;
  pages: number;
  chapters: number;
  progress: number;
  pinned: boolean;
  cover_kind: string;           // fallback generated cover (for seeds)
  cover_data?: string | null;   // dataURL of real cover if available
  accent: string;
  blurb?: string;
  file_hash: string;
  added_at: number;
  kind?: 'epub' | 'pdf' | 'txt' | 'md' | 'seed';
  lang?: string;
  position?: { ch: number; sent: number; scroll: number; ts: number };
  /** Set by the SiYuan mirror when a book is no longer present in the
   *  authoritative /Books tree - hides it from the library without wiping
   *  local chapters, so re-adding the book in SiYuan restores it. */
  hidden?: boolean;
  /** Word count per chapter. Used to compute whole-book progress as
   *  (words read / total words) instead of scroll-within-chapter. Cached
   *  on first Reader load. */
  chapter_words?: number[];
};

export type BookChapter = {
  book_id: string;
  idx: number;
  title: string;
  text: string;
};

export type LocalChunk = {
  id: string;
  book_id: string;
  chapter: number;
  page: number;
  text: string;
  vec?: Float32Array;
};

export type LocalHighlight = {
  id: string;
  book_id: string;
  chapter: number;
  page: number;
  text: string;
  note: string | null;
  kind: 'quote' | 'learning';
  status: 'pending' | 'synced';
  created_at: number;
};

export type OfflineAudio = {
  key: string;            // `${bookId}::${ch}::${sent}::${voice}`
  book_id: string;
  ch: number;
  sent: number;
  voice: string;
  bytes: number;
  blob: Blob;             // WAV audio (24kHz)
};

interface DoomDB extends DBSchema {
  books: { key: string; value: LocalBook };
  files: { key: string; value: { id: string; blob: Blob } };
  chunks: { key: string; value: LocalChunk; indexes: { 'by-book': string } };
  chapters: { key: string; value: BookChapter & { key: string }; indexes: { 'by-book': string } };
  highlights: { key: string; value: LocalHighlight; indexes: { 'by-status': string } };
  settings: { key: string; value: any };
  audio: { key: string; value: OfflineAudio; indexes: { 'by-book': string } };
}

let dbPromise: Promise<IDBPDatabase<DoomDB>> | null = null;

export function getDB() {
  if (!dbPromise) {
    dbPromise = openDB<DoomDB>('doomscroll', 3, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          db.createObjectStore('books', { keyPath: 'id' });
          db.createObjectStore('files', { keyPath: 'id' });
          const chunks = db.createObjectStore('chunks', { keyPath: 'id' });
          chunks.createIndex('by-book', 'book_id');
          const highlights = db.createObjectStore('highlights', { keyPath: 'id' });
          highlights.createIndex('by-status', 'status');
          db.createObjectStore('settings');
        }
        if (oldVersion < 2) {
          const chapters = db.createObjectStore('chapters', { keyPath: 'key' });
          chapters.createIndex('by-book', 'book_id');
        }
        if (oldVersion < 3) {
          const audio = db.createObjectStore('audio', { keyPath: 'key' });
          audio.createIndex('by-book', 'book_id');
        }
      },
    });
  }
  return dbPromise;
}

export async function listBooks(): Promise<LocalBook[]> {
  const db = await getDB();
  const all = await db.getAll('books');
  // Mirror-hidden books are excluded from the library view.
  return all.filter((b) => !b.hidden).sort((a, b) => b.added_at - a.added_at);
}

export async function putBook(book: LocalBook) {
  const db = await getDB();
  await db.put('books', book);
}

export async function getBook(id: string): Promise<LocalBook | undefined> {
  const db = await getDB();
  return db.get('books', id);
}

export async function setProgress(id: string, progress: number) {
  const db = await getDB();
  const book = await db.get('books', id);
  if (book) await db.put('books', { ...book, progress });
}

export async function addHighlight(h: LocalHighlight) {
  const db = await getDB();
  await db.put('highlights', h);
}

export async function pendingHighlights(): Promise<LocalHighlight[]> {
  const db = await getDB();
  return db.getAllFromIndex('highlights', 'by-status', 'pending');
}

export async function putChunks(chunks: LocalChunk[]) {
  const db = await getDB();
  const tx = db.transaction('chunks', 'readwrite');
  await Promise.all(chunks.map((c) => tx.store.put(c)));
  await tx.done;
}

export async function getAllChunks(): Promise<LocalChunk[]> {
  const db = await getDB();
  return db.getAll('chunks');
}

export async function getChunksByBook(bookId: string): Promise<LocalChunk[]> {
  const db = await getDB();
  return db.getAllFromIndex('chunks', 'by-book', bookId);
}

export async function putChapters(bookId: string, chapters: { idx: number; title: string; text: string }[]) {
  const db = await getDB();
  const tx = db.transaction('chapters', 'readwrite');
  await Promise.all(chapters.map((c) => tx.store.put({ key: `${bookId}-${c.idx}`, book_id: bookId, idx: c.idx, title: c.title, text: c.text })));
  await tx.done;
}

export async function getChapters(bookId: string): Promise<BookChapter[]> {
  const db = await getDB();
  const all = await db.getAllFromIndex('chapters', 'by-book', bookId);
  return all.sort((a, b) => a.idx - b.idx);
}

/** Whole-book progress = cumulative words up to current position / total words.
 *  Falls back to scroll fraction if chapter_words hasn't been cached yet. */
export function computeProgressFraction(chapter_words: number[] | undefined, ch: number, scroll: number): number {
  if (!chapter_words || !chapter_words.length) return Math.max(0, Math.min(1, scroll));
  const total = chapter_words.reduce((a, b) => a + b, 0);
  if (!total) return 0;
  const before = chapter_words.slice(0, Math.max(0, ch)).reduce((a, b) => a + b, 0);
  const current = (chapter_words[ch] || 0) * Math.max(0, Math.min(1, scroll));
  return Math.max(0, Math.min(1, (before + current) / total));
}

// Debounced React-store refresh so savePosition writes don't cause a
// re-render storm, but the store converges on the latest position within
// a couple seconds - keeps Library / BookDetail aligned mid-session.
let storeRefreshTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleStoreRefresh() {
  if (storeRefreshTimer) return;
  storeRefreshTimer = setTimeout(async () => {
    storeRefreshTimer = null;
    try {
      const { useStore } = await import('./store');
      await useStore.getState().refreshBooks();
    } catch {}
  }, 2000);
}

export async function savePosition(bookId: string, ch: number, sent: number, scroll: number) {
  const db = await getDB();
  const book = await db.get('books', bookId);
  if (!book) return;
  const progress = computeProgressFraction(book.chapter_words, ch, scroll);
  const ts = Date.now();
  await db.put('books', { ...book, position: { ch, sent, scroll, ts }, progress });
  // Align the React store so Library/BookDetail reflect the latest position
  // without needing a reload. Debounced.
  scheduleStoreRefresh();
  // Fire-and-forget remote upsert so progress survives site-data-clears
  // and cross-device switches. Fail silently if offline.
  import('./sync').then((m) => m.pushProgress(bookId, ch, sent, scroll)).catch(() => {});
}

export async function clearLibrary() {
  const db = await getDB();
  await db.clear('books');
  await db.clear('files');
  await db.clear('chapters');
  await db.clear('chunks');
  await db.clear('highlights');
}

export async function getSetting<T = unknown>(key: string): Promise<T | undefined> {
  const db = await getDB();
  return db.get('settings', key) as Promise<T | undefined>;
}

export async function setSetting(key: string, value: unknown) {
  const db = await getDB();
  await db.put('settings', value, key);
}
